import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { DemoLaunchProvider } from "@/context/DemoLaunchContext";
import { SectorProvider } from "@/context/SectorContext";
import Layout from "@/components/Layout";
import { HelpProvider } from "@/components/HelpCenter";
// Entry points stay eager for an instant first paint; everything else is route-split
// (React.lazy) so the initial marketing bundle is small and each area loads its own chunk.
import Landing from "@/pages/Landing";
import LaunchDemo from "@/pages/LaunchDemo";
import { OverflowGuard } from "@/components/ResponsiveGuards";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const StaffManagement = lazy(() => import("@/pages/StaffManagement"));
const StaffImport = lazy(() => import("@/pages/StaffImport"));
const CompleteProfile = lazy(() => import("@/pages/CompleteProfile"));
const AriActivityCentre = lazy(() => import("@/pages/AriActivityCentre"));
const Academy = lazy(() => import("@/pages/Academy"));
const Checklists = lazy(() => import("@/pages/Checklists"));
const Performance = lazy(() => import("@/pages/Performance"));
const StaffHome = lazy(() => import("@/pages/StaffHome"));
const Profile = lazy(() => import("@/pages/Profile"));
const SalesDashboard = lazy(() => import("@/pages/SalesDashboard"));
const SalesSupport = lazy(() => import("@/pages/SalesSupport"));
const Support = lazy(() => import("@/pages/Support"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
const Leads = lazy(() => import("@/pages/Leads"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminSales = lazy(() => import("@/pages/AdminSales"));
const SalesTeam = lazy(() => import("@/pages/SalesTeam"));
const ReferralSignup = lazy(() => import("@/pages/ReferralSignup"));
const CinemaShowcase = lazy(() => import("@/pages/CinemaShowcase"));
const HeroTest = lazy(() => import("@/pages/HeroTest"));
const RomainAd = lazy(() => import("@/pages/RomainAd"));
const ProductAd = lazy(() => import("@/pages/ProductAd"));
const FilmViewer = lazy(() => import("@/pages/FilmViewer"));
const Billing = lazy(() => import("@/pages/Billing"));
const PlatformDashboard = lazy(() => import("@/pages/PlatformDashboard"));
const RestaurantSupport = lazy(() => import("@/pages/RestaurantSupport"));
const Ownership = lazy(() => import("@/pages/Ownership"));
const PeoplePermissions = lazy(() => import("@/pages/PeoplePermissions"));
const SubscriptionRequired = lazy(() => import("@/pages/SubscriptionRequired"));
const AICoach = lazy(() => import("@/pages/AICoach"));
const Reception = lazy(() => import("@/pages/Reception"));
const ReceptionStationDevice = lazy(() => import("@/pages/Reception").then((m) => ({ default: m.ReceptionStationDevice })));
const Attendance = lazy(() => import("@/pages/Attendance"));
const Kiosk = lazy(() => import("@/pages/Kiosk"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const WorkforceDashboard = lazy(() => import("@/pages/WorkforceDashboard"));
const GuestExperience = lazy(() => import("@/pages/GuestExperience"));
const StaffGuestView = lazy(() => import("@/pages/StaffGuestView"));
const GuestPortal = lazy(() => import("@/pages/GuestPortal"));
const ReserveWidget = lazy(() => import("@/pages/ReserveWidget"));
const DemoRestaurant = lazy(() => import("@/pages/DemoRestaurant"));
const Reservations = lazy(() => import("@/pages/Reservations"));
const CommunicationCentre = lazy(() => import("@/pages/CommunicationCentre"));
const DemoGuide = lazy(() => import("@/pages/DemoGuide"));
const SalesAcademy = lazy(() => import("@/pages/SalesAcademy"));
const MarketingMaterials = lazy(() => import("@/pages/MarketingMaterials"));
const MarketingAssetsPrint = lazy(() => import("@/components/marketing/MarketingAssets"));
const CommissionCenter = lazy(() => import("@/pages/CommissionCenter"));
const MyCommissions = lazy(() => import("@/pages/MyCommissions"));
const Messages = lazy(() => import("@/pages/Messages"));
const EmailLogs = lazy(() => import("@/pages/EmailLogs"));
const SystemHealth = lazy(() => import("@/pages/SystemHealth"));
const Rota = lazy(() => import("@/pages/Rota"));
const ReceiptIntelligence = lazy(() => import("@/pages/ReceiptIntelligence"));
const OperationsBrain = lazy(() => import("@/pages/OperationsBrain"));
const Workshop = lazy(() => import("@/pages/Workshop"));
const CapabilityHome = lazy(() => import("@/pages/CapabilityHome"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const Offer = lazy(() => import("@/pages/Offer"));
const CustomerSupport = lazy(() => import("@/pages/CustomerSupport"));
const AffiliateResources = lazy(() => import("@/pages/AffiliateResources"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const CustomersAdmin = lazy(() => import("@/pages/admin/CustomersAdmin"));
const LegalCenter = lazy(() => import("@/pages/legal/LegalCenter"));
const LegalDocument = lazy(() => import("@/pages/legal/LegalDocument"));
const LegalAdmin = lazy(() => import("@/pages/admin/LegalAdmin"));
const PartnerRecruitment = lazy(() => import("@/pages/partners/PartnerRecruitment"));
const PartnerOnboarding = lazy(() => import("@/pages/partners/PartnerOnboarding"));
const RecruitmentAdmin = lazy(() => import("@/pages/admin/RecruitmentAdmin"));
const PaymentProfile = lazy(() => import("@/pages/PaymentProfile"));
const Settings = lazy(() => import("@/pages/Settings"));
const PaymentProfilesAdmin = lazy(() => import("@/pages/admin/PaymentProfilesAdmin"));
const CommissionApproval = lazy(() => import("@/pages/admin/CommissionApproval"));
const FunnelAdmin = lazy(() => import("@/pages/admin/FunnelAdmin"));
const homeFor = (role) =>
  role === "staff" ? "/me" : role === "salesperson" ? "/sales" : role === "super_admin" ? "/platform" : role === "sales_applicant" ? "/onboarding" : "/dashboard";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
      <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-[#D4AF37] animate-spin" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const portal = sessionStorage.getItem("si_login_portal");
        sessionStorage.removeItem("si_login_portal");
        const { data } = await api.post("/auth/session", { session_id: sid, ...(portal ? { portal } : {}) });
        localStorage.setItem("si_token", data.token);
        setUser(data.user);
        window.history.replaceState(null, "", "/");
        navigate(homeFor(data.user.role), { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail;
        if (typeof msg === "string") toast.error(msg);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Loader />;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function Gated({ children }) {
  const { user } = useAuth();
  if (user.subscription_exempt || user.subscription_active) return children;
  if (user.role === "owner") return <Navigate to="/billing" replace />;
  return <SubscriptionRequired />;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Landing />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    // Always start new routes at the top (fixes hero/cinematic opening mid-page or at the bottom).
    if (!location.hash) window.scrollTo(0, 0);
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/experience" element={<LaunchDemo />} />
      <Route path="/g/:token" element={<GuestPortal />} />
      <Route path="/rw/:publicId" element={<ReserveWidget />} />
      <Route path="/demo-restaurant" element={<DemoRestaurant />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/offer" element={<Offer />} />
      <Route path="/customer-support" element={<CustomerSupport />} />
      <Route path="/affiliate-resources" element={<AffiliateResources />} />
      <Route path="/legal" element={<LegalCenter />} />
      <Route path="/legal/:slug" element={<LegalDocument />} />
      <Route path="/partners" element={<PartnerRecruitment />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/reception/station" element={<ReceptionStationDevice />} />
      <Route path="/subscription-success" element={<Protected><SubscriptionSuccess /></Protected>} />
      <Route path="/signup" element={<ReferralSignup />} />
      <Route path="/cinema" element={<CinemaShowcase />} />
      {process.env.REACT_APP_ENABLE_CAMPAIGN_PREVIEW === "true" && (
        <>
          <Route path="/hero-test" element={<HeroTest />} />
          <Route path="/romain-ad" element={<RomainAd />} />
          <Route path="/product-ad" element={<ProductAd />} />
          <Route path="/film" element={<FilmViewer />} />
        </>
      )}
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Protected roles={["manager", "owner"]}><Gated><Dashboard /></Gated></Protected>} />
        <Route path="/staff" element={<Protected roles={["manager", "owner"]}><Gated><StaffManagement /></Gated></Protected>} />
        <Route path="/staff/import" element={<Protected roles={["manager", "owner"]}><Gated><StaffImport /></Gated></Protected>} />
        <Route path="/training" element={<Navigate to="/academy" replace />} />
        <Route path="/checklists" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Checklists /></Gated></Protected>} />
        <Route path="/performance" element={<Protected roles={["manager", "owner"]}><Gated><Performance /></Gated></Protected>} />
        <Route path="/ownership" element={<Protected roles={["owner", "manager"]}><Gated><Ownership /></Gated></Protected>} />
        <Route path="/people" element={<Protected roles={["owner", "manager"]}><Gated><PeoplePermissions /></Gated></Protected>} />
        <Route path="/sales" element={<Protected roles={["salesperson"]}><SalesDashboard /></Protected>} />
        <Route path="/sales/support" element={<Protected roles={["salesperson"]}><SalesSupport /></Protected>} />
        <Route path="/support" element={<Protected roles={["owner", "manager"]}><Support /></Protected>} />
        <Route path="/admin/support" element={<Protected roles={["super_admin"]}><AdminSupport /></Protected>} />
        <Route path="/sales/commissions" element={<Protected roles={["salesperson"]}><MyCommissions /></Protected>} />
        <Route path="/sales/demo-guide" element={<Protected roles={["salesperson", "super_admin"]}><DemoGuide /></Protected>} />
        <Route path="/sales/academy" element={<Protected roles={["salesperson", "super_admin", "sales_applicant"]}><SalesAcademy /></Protected>} />
        <Route path="/sales/materials" element={<Protected roles={["salesperson", "super_admin"]}><MarketingMaterials /></Protected>} />
        <Route path="/onboarding" element={<Protected roles={["sales_applicant"]}><PartnerOnboarding /></Protected>} />
        <Route path="/admin/recruitment" element={<Protected roles={["super_admin"]}><RecruitmentAdmin /></Protected>} />
        <Route path="/sales/payment-profile" element={<Protected roles={["salesperson"]}><PaymentProfile /></Protected>} />
        <Route path="/admin/payments" element={<Protected roles={["super_admin"]}><PaymentProfilesAdmin /></Protected>} />
        <Route path="/admin/commission-approval" element={<Protected roles={["super_admin"]}><CommissionApproval /></Protected>} />
        <Route path="/leads" element={<Protected roles={["salesperson", "super_admin"]}><Leads /></Protected>} />
        <Route path="/admin/sales" element={<Protected roles={["super_admin"]}><AdminSales /></Protected>} />
        <Route path="/admin/system-health" element={<Protected roles={["super_admin"]}><SystemHealth /></Protected>} />
        <Route path="/admin/sales-team" element={<Protected roles={["super_admin"]}><SalesTeam /></Protected>} />
        <Route path="/admin/commissions" element={<Protected roles={["super_admin"]}><CommissionCenter /></Protected>} />
        <Route path="/platform" element={<Protected roles={["super_admin"]}><PlatformDashboard /></Protected>} />
        <Route path="/admin/customers" element={<Protected roles={["super_admin"]}><CustomersAdmin /></Protected>} />
        <Route path="/admin/legal" element={<Protected roles={["super_admin"]}><LegalAdmin /></Protected>} />
        <Route path="/platform/restaurant/:rid" element={<Protected roles={["super_admin"]}><RestaurantSupport /></Protected>} />
        <Route path="/billing" element={<Protected roles={["owner", "super_admin"]}><Billing /></Protected>} />
        <Route path="/me" element={<Protected roles={["manager", "owner", "staff"]}><Gated><StaffHome /></Gated></Protected>} />
        <Route path="/coach" element={<Protected roles={["manager", "owner", "staff"]}><Gated><AICoach /></Gated></Protected>} />
        <Route path="/attendance" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Attendance /></Gated></Protected>} />
        <Route path="/payroll" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Payroll /></Gated></Protected>} />
        <Route path="/workforce" element={<Protected roles={["manager", "owner"]}><Gated><WorkforceDashboard /></Gated></Protected>} />
        <Route path="/guest" element={<Protected roles={["manager", "owner"]}><Gated><GuestExperience /></Gated></Protected>} />
        <Route path="/reservations" element={<Protected roles={["manager", "owner"]}><Gated><Reservations /></Gated></Protected>} />
        <Route path="/staff/guest" element={<Protected roles={["manager", "owner", "staff"]}><Gated><StaffGuestView /></Gated></Protected>} />
        <Route path="/reception" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Reception /></Gated></Protected>} />
        <Route path="/communication" element={<Protected roles={["manager", "owner"]}><Gated><CommunicationCentre /></Gated></Protected>} />
        <Route path="/messages" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Messages /></Gated></Protected>} />
        <Route path="/email-logs" element={<Protected roles={["manager", "owner"]}><Gated><EmailLogs /></Gated></Protected>} />
        <Route path="/rota" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Rota /></Gated></Protected>} />
        <Route path="/receipts" element={<Protected roles={["manager", "owner"]}><Gated><ReceiptIntelligence /></Gated></Protected>} />
        <Route path="/ops-brain" element={<Protected roles={["manager", "owner"]}><Gated><OperationsBrain /></Gated></Protected>} />
        <Route path="/workshop" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Workshop /></Gated></Protected>} />
        <Route path="/capability/:projectId" element={<Protected roles={["manager", "owner", "staff"]}><Gated><CapabilityHome /></Gated></Protected>} />
        <Route path="/academy" element={<Protected roles={["manager", "owner", "staff"]}><Gated><Academy /></Gated></Protected>} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/complete-profile" element={<Protected roles={["manager", "owner", "staff"]}><Gated><CompleteProfile /></Gated></Protected>} />
        <Route path="/ari" element={<Protected roles={["manager", "owner", "staff"]}><Gated><AriActivityCentre /></Gated></Protected>} />
        <Route path="/settings" element={<Protected roles={["manager", "owner", "staff"]}><Settings /></Protected>} />
      </Route>
      <Route path="/admin/funnel" element={<FunnelAdmin />} />
      <Route path="/print/marketing-assets" element={<MarketingAssetsPrint />} />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <DemoLaunchProvider>
          <LanguageProvider>
            <HelpProvider>
              <SectorProvider>
                <AppRouter />
              </SectorProvider>
            </HelpProvider>
          </LanguageProvider>
          </DemoLaunchProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <OverflowGuard />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
